import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Users, Plus, Search, Shield, MapPin, ChevronRight, Layers, Trophy,
  Settings, ArrowRight, Building2, Filter,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SPORT_COLORS = {
  Basketball: '#ef4444',
  Volleyball: '#3b82f6',
  Soccer: '#22c55e',
  Football: '#f59e0b',
  Baseball: '#8b5cf6',
  Softball: '#ec4899',
  Lacrosse: '#06b6d4',
};

const Teams = () => {
  const { token, user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [myPrograms, setMyPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sportFilter, setSportFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  // Create form state
  const [newProgram, setNewProgram] = useState({
    name: '', slug: '', sport: 'Basketball', description: '',
    primary_color: '#06b6d4', city: '', state: '',
  });

  const fetchPrograms = useCallback(async () => {
    if (!token) return;
    try {
      // Only fetch programs the user is connected to
      const res = await fetch(`${API_URL}/api/programs/my-programs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        let data = await res.json();
        // Apply local search/sport filter
        if (search) {
          const s = search.toLowerCase();
          data = data.filter(p => p.name?.toLowerCase().includes(s) || p.slug?.toLowerCase().includes(s) || p.city?.toLowerCase().includes(s));
        }
        if (sportFilter) {
          data = data.filter(p => p.sport?.toLowerCase() === sportFilter.toLowerCase());
        }
        setPrograms(data);
        setMyPrograms(data);
      }
    } catch { /* ignore */ }
  }, [search, sportFilter, token]);

  const fetchMyPrograms = useCallback(async () => {
    // Already handled above
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchPrograms(), fetchMyPrograms()]);
      setLoading(false);
    };
    load();
  }, [fetchPrograms, fetchMyPrograms]);

  const createProgram = async () => {
    if (!newProgram.name || !newProgram.slug) {
      toast.error('Name and URL slug are required');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/programs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(newProgram),
      });
      if (res.ok) {
        const prog = await res.json();
        toast.success('Program created!');
        setShowCreate(false);
        setNewProgram({ name: '', slug: '', sport: 'Basketball', description: '', primary_color: '#06b6d4', city: '', state: '' });
        fetchPrograms();
        fetchMyPrograms();
        navigate(`/teams/${prog.slug}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create');
      }
    } catch { toast.error('Network error'); }
  };

  const ProgramCard = ({ program, isMine }) => {
    const color = program.primary_color || SPORT_COLORS[program.sport] || '#06b6d4';
    const initials = program.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

    return (
      <Link to={`/teams/${program.slug}`} data-testid={`program-card-${program.slug}`}>
        <Card className="bg-zinc-900 border-zinc-800 hover:border-zinc-600 transition-all group cursor-pointer overflow-hidden">
          {/* Top accent bar */}
          <div className="h-1" style={{ backgroundColor: color }} />
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Avatar className="h-11 w-11 shrink-0 rounded-lg">
                {program.logo_url ? (
                  <img src={program.logo_url} alt="" className="object-cover" />
                ) : (
                  <AvatarFallback className="rounded-lg text-sm font-bold text-white" style={{ backgroundColor: color }}>
                    {initials}
                  </AvatarFallback>
                )}
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <h3 className="text-white font-semibold text-sm truncate group-hover:text-cyan-400 transition-colors">
                    {program.name}
                  </h3>
                  {isMine && (
                    <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-cyan-800 text-cyan-400 shrink-0">
                      {program.my_role === 'program_admin' ? 'Admin' : program.my_role === 'coach' ? 'Coach' : 'Member'}
                    </Badge>
                  )}
                </div>
                {program.sport && (
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-zinc-800 text-zinc-400 mb-1">
                    {program.sport}
                  </Badge>
                )}
                <div className="flex items-center gap-3 text-[11px] text-zinc-500 mt-1">
                  {program.city && (
                    <span className="flex items-center gap-0.5">
                      <MapPin className="w-3 h-3" /> {program.city}, {program.state}
                    </span>
                  )}
                  {program.team_count > 0 && (
                    <span className="flex items-center gap-0.5">
                      <Layers className="w-3 h-3" /> {program.team_count} teams
                    </span>
                  )}
                  {program.member_count > 0 && (
                    <span className="flex items-center gap-0.5">
                      <Users className="w-3 h-3" /> {program.member_count}
                    </span>
                  )}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400 transition-colors shrink-0 mt-1" />
            </div>
          </CardContent>
        </Card>
      </Link>
    );
  };

  // Auth gate — only visible to logged-in users
  if (!authLoading && !isAuthenticated()) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
        <div className="text-center max-w-sm" data-testid="teams-auth-gate">
          <Shield className="w-12 h-12 text-cyan-500/40 mx-auto mb-4" />
          <h2 className="text-white font-bold text-xl mb-2">Sign in to view Programs</h2>
          <p className="text-zinc-500 text-sm mb-6">
            The Teams directory is only available to registered Rooam Sports members.
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate('/login')} className="bg-cyan-600 hover:bg-cyan-500 text-white font-medium text-sm" data-testid="teams-login-btn">
              Sign In
            </Button>
            <Button onClick={() => navigate('/register')} variant="outline" className="border-zinc-700 text-zinc-300 text-sm" data-testid="teams-register-btn">
              Create Account
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-6 h-6 text-cyan-500" />
              <h1 className="text-3xl font-bold text-white" data-testid="teams-heading">Rooam Teams</h1>
            </div>
            <p className="text-zinc-500 text-sm">Programs you're connected to</p>
          </div>
          {isAuthenticated() && (
            <Button
              onClick={() => setShowCreate(!showCreate)}
              className="bg-cyan-600 hover:bg-cyan-500 text-white font-medium"
              data-testid="create-program-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> New Program
            </Button>
          )}
        </div>

        {/* Create Program Form */}
        {showCreate && (
          <Card className="bg-zinc-900 border-zinc-800 mb-6" data-testid="create-program-form">
            <CardContent className="p-5">
              <h2 className="text-white font-semibold text-lg mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-cyan-500" /> Create New Program
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Program Name *</label>
                  <Input
                    value={newProgram.name}
                    onChange={e => {
                      const name = e.target.value;
                      setNewProgram(p => ({
                        ...p, name,
                        slug: name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
                      }));
                    }}
                    placeholder="Metro Youth Basketball"
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="program-name-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">URL Slug</label>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-zinc-600">/teams/</span>
                    <Input
                      value={newProgram.slug}
                      onChange={e => setNewProgram(p => ({ ...p, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                      placeholder="metro-youth-bball"
                      className="bg-zinc-800 border-zinc-700 text-white flex-1"
                      data-testid="program-slug-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Sport</label>
                  <select
                    value={newProgram.sport}
                    onChange={e => setNewProgram(p => ({ ...p, sport: e.target.value }))}
                    className="w-full h-9 px-3 rounded-md bg-zinc-800 border border-zinc-700 text-white text-sm"
                    data-testid="program-sport-select"
                  >
                    {Object.keys(SPORT_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Brand Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={newProgram.primary_color}
                      onChange={e => setNewProgram(p => ({ ...p, primary_color: e.target.value }))}
                      className="w-9 h-9 rounded cursor-pointer bg-transparent border-0"
                    />
                    <Input
                      value={newProgram.primary_color}
                      onChange={e => setNewProgram(p => ({ ...p, primary_color: e.target.value }))}
                      className="bg-zinc-800 border-zinc-700 text-white flex-1"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">City</label>
                  <Input
                    value={newProgram.city}
                    onChange={e => setNewProgram(p => ({ ...p, city: e.target.value }))}
                    placeholder="Charlotte"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">State</label>
                  <Input
                    value={newProgram.state}
                    onChange={e => setNewProgram(p => ({ ...p, state: e.target.value }))}
                    placeholder="NC"
                    className="bg-zinc-800 border-zinc-700 text-white"
                    maxLength={2}
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="text-xs text-zinc-500 mb-1 block">Description</label>
                <Input
                  value={newProgram.description}
                  onChange={e => setNewProgram(p => ({ ...p, description: e.target.value }))}
                  placeholder="Brief description of your program"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowCreate(false)} className="border-zinc-700 text-zinc-400">
                  Cancel
                </Button>
                <Button onClick={createProgram} className="bg-cyan-600 hover:bg-cyan-500 text-white" data-testid="submit-program-btn">
                  Create Program
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search & Filter */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search your programs..."
                className="bg-zinc-900 border-zinc-800 text-white pl-9"
                data-testid="program-search-input"
              />
            </div>
            <select
              value={sportFilter}
              onChange={e => setSportFilter(e.target.value)}
              className="h-9 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-400 text-sm"
              data-testid="sport-filter-select"
            >
              <option value="">All Sports</option>
              {Object.keys(SPORT_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {/* Program Grid */}
        {loading ? (
          <div className="text-center py-16">
            <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">Loading programs...</p>
          </div>
        ) : programs.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-programs">
            <Building2 className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 mb-2">You're not connected to any programs yet</p>
            {isAuthenticated() && (
              <Button onClick={() => setShowCreate(true)} variant="outline" className="border-zinc-700 text-zinc-400">
                <Plus className="w-4 h-4 mr-1" /> Create your first program
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="my-programs-grid">
            {programs.map(p => (
              <ProgramCard key={p.id} program={p} isMine />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Teams;
